import React, { useEffect } from 'react';
import {
  IonPage,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonContent,
  IonButtons,
  IonBackButton,
  IonSpinner,
} from '@ionic/react';
import SnowballMenu from '@/modules/user/SnowballMenu';
import PageRefresher from '@/components/PageRefresher';
import type { TopicTypeId } from '@/modules/topic/types';
import { useTopicUpsert } from '@/modules/topic/upsert/useTopicUpsert';
import UpdateToolbar from '@/modules/topic/upsert/UpdateToolbar';
import NotFoundCard from '@/modules/topic/read/NotFoundCard';
import PrivateInvitePlaceholder from '@/modules/topic/read/PrivateInvitePlaceholder';
import { useTopicReadInterstitials } from '@/modules/topic/read/useTopicReadInterstitials';
import { pageBackground } from '@/modules/topic/read/styles';
import { topicComponentRegistry } from './registry';

interface TopicCreateOrUpdatePageProps {
  initialMode: 'create' | 'update';
  topicId?: string;
  /** The chosen type in create mode (from the pre-create type picker). */
  createType?: TopicTypeId;
}

/**
 * The stable component-driven create/edit page shell — the mirror of
 * `TopicReadPage`. It owns the `IonPage` frame, the header/toolbar, the
 * action sheet, and the phone-verification / private-invite interstitials, and
 * stays mounted across the masked → loaded lifecycle so unlocking never remounts
 * the interstitial host (#524).
 *
 * The type is fixed for the whole page: in create mode it comes from the pre-create
 * type picker (`createType`), in update mode from the loaded topic. Once known, the
 * shell dispatches the scrollable form to that type's own `<type>/EditForm`, which
 * inlines the editable sections and may diverge per type.
 */
const TopicCreateOrUpdatePage: React.FC<TopicCreateOrUpdatePageProps> = ({
  initialMode,
  topicId,
  createType,
}) => {
  const upsert = useTopicUpsert({ initialMode, topicId });
  const {
    topic,
    loading,
    saving,
    topicTitle,
    endBeforeStart,
    hasChanges,
    setType,
    fetchTopicInfo,
    handleCancelUpdate,
    updateTopic,
  } = upsert;

  const isCreateMode = initialMode === 'create';

  // Seed the picked type into the form for create. `useTopicController` runs
  // `resetCreateForm()` (→ type 'event') on mount before this effect, so this is
  // the authoritative seed; the form has no type-selection UI to change it after.
  useEffect(() => {
    if (isCreateMode && createType) {
      setType(createType);
    }
  }, [isCreateMode, createType, setType]);

  const {
    invitePreviewData,
    privateInviteResolved,
    goToProfileWizardForInvite,
    interstitials,
  } = useTopicReadInterstitials({
    topicId,
    topic,
    loading,
    initialMode,
    onTopicBecameReadable: fetchTopicInfo,
  });

  const body = () => {
    if (isCreateMode) {
      const EditForm = createType
        ? topicComponentRegistry[createType]?.EditForm
        : undefined;
      if (!EditForm) {
        return <NotFoundCard />;
      }
      return <EditForm upsert={upsert} isCreateMode topicId={topicId} />;
    }
    if (loading || (!topic && !invitePreviewData && !privateInviteResolved)) {
      return (
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100%',
          }}
        >
          <IonSpinner name="crescent" />
        </div>
      );
    }
    if (!topic && invitePreviewData) {
      return (
        <PrivateInvitePlaceholder
          preview={invitePreviewData}
          onLinkPhone={goToProfileWizardForInvite}
        />
      );
    }
    if (!topic) {
      return <NotFoundCard />;
    }
    const EditForm = topicComponentRegistry[topic.type]?.EditForm;
    if (!EditForm) {
      return <NotFoundCard />;
    }
    return <EditForm upsert={upsert} isCreateMode={false} topicId={topicId} />;
  };

  return (
    <IonPage>
      <IonHeader>
        {isCreateMode ? (
          <IonToolbar>
            <IonButtons slot="start">
              <IonBackButton defaultHref="/topics" />
            </IonButtons>
            <IonTitle>Create Topic</IonTitle>
            <IonButtons slot="end">
              <SnowballMenu />
            </IonButtons>
          </IonToolbar>
        ) : (
          <UpdateToolbar
            topicId={topicId}
            saving={saving}
            topicTitle={topicTitle}
            endBeforeStart={endBeforeStart}
            hasChanges={hasChanges}
            onCancel={handleCancelUpdate}
            onUpdate={updateTopic}
          />
        )}
      </IonHeader>
      <IonContent style={pageBackground}>
        <PageRefresher />
        {body()}
      </IonContent>
      {interstitials}
    </IonPage>
  );
};

export default TopicCreateOrUpdatePage;
